import type { Key9Puzzle, Key9Result } from '../types/puzzle';

export const KEY9_DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

export type Key9LockedCellProgress = {
  cell: number;
  digit: number;
};

export type Key9ProgressSnapshot = {
  misses: number;
  hints: number;
  seconds: number;
  lockedCells: Key9LockedCellProgress[];
};

export type RestoredKey9ProgressState = {
  misses: number;
  hints: number;
  seconds: number;
  lockedCells: Map<number, number>;
  usedTiles: Set<number>;
  undoStack: { cell: number; tileIndex: number; digit: number }[];
};

export function normalizeKey9SolvedDigits(value: unknown, status?: Key9Result['status']): number[] {
  if (status && status !== 'missed') return [...KEY9_DIGITS];
  if (!Array.isArray(value)) return [];

  return Array.from(new Set(value
    .map((digit) => Math.floor(Number(digit)))
    .filter((digit) => Number.isInteger(digit) && digit >= 1 && digit <= 9)))
    .sort((a, b) => a - b);
}

export function normalizeKey9Result(result: Key9Result): Key9Result {
  return {
    ...result,
    key9SolvedDigits: normalizeKey9SolvedDigits(result.key9SolvedDigits, result.status)
  };
}

export function collectKey9SolvedDigits(lockedCells: Map<number, number>): number[] {
  return normalizeKey9SolvedDigits(Array.from(lockedCells.values()));
}

export function createKey9ProgressSnapshot(input: {
  misses: number;
  hints: number;
  seconds: number;
  lockedCells: Map<number, number>;
}): Key9ProgressSnapshot {
  return normalizeKey9ProgressSnapshot({
    misses: input.misses,
    hints: input.hints,
    seconds: input.seconds,
    lockedCells: Array.from(input.lockedCells, ([cell, digit]) => ({ cell, digit }))
  });
}

export function normalizeKey9ProgressSnapshot(value: unknown): Key9ProgressSnapshot {
  const candidate = value && typeof value === 'object'
    ? value as Partial<Key9ProgressSnapshot>
    : {};
  const lockedCells: Key9LockedCellProgress[] = [];
  const usedCells = new Set<number>();
  const usedDigits = new Set<number>();

  if (Array.isArray(candidate.lockedCells)) {
    candidate.lockedCells.forEach((entry) => {
      if (!entry || typeof entry !== 'object') return;
      const cell = Math.floor(Number((entry as Key9LockedCellProgress).cell));
      const digit = Math.floor(Number((entry as Key9LockedCellProgress).digit));
      if (!Number.isInteger(cell) || cell < 0 || cell > 80) return;
      if (!Number.isInteger(digit) || digit < 1 || digit > 9) return;
      if (usedCells.has(cell) || usedDigits.has(digit)) return;
      usedCells.add(cell);
      usedDigits.add(digit);
      lockedCells.push({ cell, digit });
    });
  }

  lockedCells.sort((a, b) => a.cell - b.cell);
  return {
    misses: Math.min(8, normalizeNonNegativeInteger(candidate.misses)),
    hints: normalizeNonNegativeInteger(candidate.hints),
    seconds: normalizeNonNegativeInteger(candidate.seconds),
    lockedCells
  };
}

export function restoreKey9ProgressState(
  puzzle: Key9Puzzle,
  value: unknown
): RestoredKey9ProgressState {
  const snapshot = normalizeKey9ProgressSnapshot(value);
  const keyCells = new Set(puzzle.keyCells);
  const lockedCells = new Map<number, number>();
  const usedTiles = new Set<number>();
  const undoStack: RestoredKey9ProgressState['undoStack'] = [];

  snapshot.lockedCells.forEach(({ cell, digit }) => {
    const tileIndex = puzzle.keyTiles.indexOf(digit);
    if (!keyCells.has(cell)) return;
    if (puzzle.solution[cell] !== String(digit)) return;
    if (tileIndex < 0 || usedTiles.has(tileIndex)) return;
    lockedCells.set(cell, digit);
    usedTiles.add(tileIndex);
    undoStack.push({ cell, tileIndex, digit });
  });

  return {
    misses: snapshot.misses,
    hints: snapshot.hints,
    seconds: snapshot.seconds,
    lockedCells,
    usedTiles,
    undoStack
  };
}

function normalizeNonNegativeInteger(value: unknown): number {
  return Math.max(0, Math.floor(Number(value) || 0));
}
